/* eslint-disable react/no-unescaped-entities, no-empty */

/**
 * AgentCockpitPage.js — Phase 2 refonte UX "Dark Luxury"
 * ======================================================
 * Cockpit Performance Agent refondu pour le langage visuel V1 (Dark Luxury).
 * Conserve l'intégralité du data flow (/api/cockpit-agent), les 5 onglets
 * existants et toutes les features (HomlyScoreWidget, MyCoachingTab, etc.).
 * Apporte :
 *   • PageHeader Dark Luxury avec kicker orange + greeting horaire
 *   • KPI tiles refaites en utilisant KPIWithExplanation
 *   • Priorités via ActionCard (palette urgent/important/low cohérente)
 *   • Empty states via le composant EmptyState
 *   • Panels en bg-elevated + border subtile (cohérent sidebar)
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
    Sparkles, Target, Euro, Home, Clock, Mail, FileText, AlertTriangle,
    Calendar, Phone, ArrowRight, CheckCircle2, Loader2, Activity,
    Send, Trophy, FolderClosed,
} from 'lucide-react';
import TermTooltip from '../../components/help/TermTooltip';
import GlossaryHint from '../../components/help/GlossaryHint';
import AIChatModal from '../../components/ai/AIChatModal';
import DataQualityBanner from '../../components/dashboard/DataQualityBanner';
import HomlyScoreWidget from '../../components/dashboard/HomlyScoreWidget';
import WeeklySuccessCard from '../../components/engagement/WeeklySuccessCard';
import ProgressMomentumBand from '../../components/engagement/ProgressMomentumBand';
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import WeeklyRecapBanner from '../../components/cockpit/WeeklyRecapBanner';
import MyMilestonesSection from '../../components/cockpit/MyMilestonesSection';
import MyCoachingTab from '../../components/dashboard/MyCoachingTab';
import AgentActionsWidget from '../../components/pilotage/AgentActionsWidget';
import {
    PageHeader, EmptyState, ActionCard, KPIWithExplanation, SectionBlock, HomlyButton, PriorityBadge,
} from '../../components/homly';

const ICON_MAP = { Clock, Mail, FileText, Target, Phone, Calendar, Euro };

function fmtEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(s) {
    if (!s) return '';
    try {
        return new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch { return s; }
}

// ─── Risk row Dark Luxury ─────────────────────────────────────────────
function RiskRow({ m, basePath }) {
    const sev = m.severity >= 3 ? 'critical' : m.severity === 2 ? 'high' : 'medium';
    const sevColor = sev === 'critical' ? 'var(--homly-priority-critical)' : sev === 'high' ? 'var(--homly-priority-high)' : 'var(--homly-priority-medium)';
    return (
        <Link
            to={`${basePath}/mandates/v2/${m.mandate_id}`}
            data-testid={`risk-${m.mandate_id}`}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                background: 'transparent',
                borderBottom: '1px solid var(--homly-border-subtle)',
                textDecoration: 'none',
                transition: 'background var(--homly-transition-fast)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: sevColor,
                    boxShadow: `0 0 8px ${sevColor}`,
                    flexShrink: 0,
                }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--homly-text-primary)', fontFamily: 'var(--homly-font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.address}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--homly-text-tertiary)', marginTop: 2 }}>
                    {m.silence_days}j sans action · {m.age_days}j d'ancienneté
                </div>
            </div>
            <ArrowRight size={14} strokeWidth={2.2} style={{ color: 'var(--homly-text-tertiary)', flexShrink: 0 }} />
        </Link>
    );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function AgentCockpitPage() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recapSending, setRecapSending] = useState(false);
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const stored = localStorage.getItem('homly_agent_cockpit_tab') || 'priorities';
            // Ancien onglet 'ask-ai' supprimé → redirige vers priorités
            return stored === 'ask-ai' ? 'priorities' : stored;
        }
        catch { return 'priorities'; }
    });

    const handleTabChange = (val) => {
        // Ancien onglet 'ask-ai' désormais en modale
        if (val === 'ask-ai') {
            setAiChatOpen(true);
            return;
        }
        setActiveTab(val);
        try { localStorage.setItem('homly_agent_cockpit_tab', val); } catch {}
    };

    const sendRecapNow = async () => {
        setRecapSending(true);
        try {
            const res = await api.post('/cockpit-agent/recap-now');
            if (res.data?.skipped) {
                toast.info("Aucune activité enregistrée aujourd'hui");
            } else {
                toast.success("Recap envoyé sur ta boîte mail");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de l'envoi");
        } finally {
            setRecapSending(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await api.get('/cockpit-agent');
                if (mounted) setData(res.data);
            } catch (e) {
                if (mounted) setError(e?.response?.data?.detail || 'Erreur de chargement');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <div
                data-testid="cockpit-loading"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: 12,
                    fontFamily: 'var(--homly-font-body)',
                }}
            >
                <Loader2 size={32} className="animate-spin" style={{ color: 'var(--homly-purple)' }} />
                <div style={{ color: 'var(--homly-text-secondary)' }}>Chargement de votre cockpit…</div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={{ maxWidth: 560, margin: '40px auto', padding: 24 }}>
                <div
                    style={{
                        background: 'var(--homly-priority-critical-bg)',
                        border: '1px solid var(--homly-priority-critical)',
                        borderRadius: 'var(--homly-radius-lg)',
                        padding: 20,
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                        fontFamily: 'var(--homly-font-body)',
                    }}
                >
                    <AlertTriangle size={20} style={{ color: 'var(--homly-priority-critical)', flexShrink: 0, marginTop: 2 }} />
                    <div style={{ fontSize: 14, color: 'var(--homly-text-primary)' }}>{error || 'Erreur inconnue'}</div>
                </div>
            </div>
        );
    }

    const { agent, kpis, target, priorities, at_risk_mandates, at_risk_count, week_activity, streak_days, upcoming_visits } = data;
    const basePath = agent.role === 'admin' ? '/admin' : '/agent';

    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 12) return 'Bonjour';
        if (h < 18) return 'Bon après-midi';
        return 'Bonsoir';
    })();

    const targetExplanation = target.ca_target > 0 && target.achievement_pct !== null
        ? `${target.on_track ? 'Sur la bonne voie' : 'En retard'} · objectif ${fmtEUR(target.ca_target)}`
        : 'Aucun objectif défini pour ce mois';

    const caExplanation = kpis.ca_month > 0
        ? `Sur ${fmtEUR(kpis.ca_month)} encaissé ce mois. Continuez à activer vos mandats les plus matures.`
        : `Lancez une relance ce matin pour activer vos premiers encaissements.`;

    const mandatesExplanation = kpis.active_mandates > 0
        ? `Pipeline pondéré : ${fmtEUR(kpis.weighted_pipeline)}. Concentrez-vous sur les 3 dossiers les plus mûrs.`
        : `Aucun mandat actif. Référencez-en un pour activer le suivi IA.`;

    const atRiskExplanation = at_risk_count === 0
        ? 'Aucun signal d\'alerte cette semaine. Maintenez la cadence.'
        : at_risk_count >= 5
            ? `${at_risk_count} mandats nécessitent une action urgente. Priorisez les rouges en bas.`
            : `${at_risk_count} mandat${at_risk_count > 1 ? 's' : ''} demande${at_risk_count > 1 ? 'nt' : ''} un point cette semaine.`;

    return (
        <div data-testid="agent-cockpit" style={{ maxWidth: 1280, margin: '0 auto', fontFamily: 'var(--homly-font-body)' }}>
            {/* Header Dark Luxury */}
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                <PageHeader
                    kicker={`${greeting}, ${agent.first_name} — voici votre journée`}
                    title={
                        priorities.length + at_risk_count > 0
                            ? `${priorities.length + at_risk_count} action${priorities.length + at_risk_count > 1 ? 's' : ''} prioritaire${priorities.length + at_risk_count > 1 ? 's' : ''}`
                            : 'Tout est sous contrôle aujourd\'hui'
                    }
                    subtitle={
                        priorities.length + at_risk_count > 0
                            ? `Triées par impact business par l'IA. Commencez par celles d'en haut.`
                            : `Aucune urgence détectée. Vous pouvez prospecter sereinement.`
                    }
                    primaryAction={{
                        label: 'Recap email du jour',
                        icon: Send,
                        onClick: sendRecapNow,
                    }}
                    secondaryAction={{
                        label: 'Demander à l\'IA',
                        icon: Sparkles,
                        onClick: () => setAiChatOpen(true),
                    }}
                    dataTestid="agent-cockpit-header"
                />
            </motion.div>

            {/* Bandeau "orienté progrès" — maîtrise · régularité · élan (vs soi) */}
            <ProgressMomentumBand />

            {/* Encart hebdomadaire chiffré — P5 */}
            <div className="mt-4">
                <WeeklyRecapBanner scope="mine" />
            </div>

            {/* Vue synthétique — 5 bouquets KPI contextuels (P3) */}
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="mt-4"
                        data-testid="agent-bouquets-strip">
                <KPIBouquetsStrip scope="mine" variant="agent" />
            </motion.div>

            {/* Mes paliers — visuels partageables story / photo */}
            <MyMilestonesSection />

            {/* Accès rapides — nouveaux modules P2 (P4) */}
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.08 }}
                        className="mt-4"
                        data-testid="agent-quick-access">
                <div className="bg-gradient-to-br from-[#681793]/15 to-[#FF914D]/15 rounded-2xl p-5 border border-[#681793]/30">
                    <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FF914D]" />
                        Mes outils
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                        <Link to="/notaire/dossiers" data-testid="agent-nav-notary"
                              className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-[#681793]/50 transition">
                            <FolderClosed className="w-5 h-5 text-[#681793]" />
                            <div>
                                <p className="text-sm font-semibold text-foreground">Dossiers notaire</p>
                                <p className="text-xs text-muted-foreground">Checklist des pièces + partage notaire</p>
                            </div>
                        </Link>
                        <Link to="/agent/commissions" data-testid="agent-nav-commissions"
                              className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-[#681793]/50 transition">
                            <Euro className="w-5 h-5 text-[#FF914D]" />
                            <div>
                                <p className="text-sm font-semibold text-foreground">Mes commissions</p>
                                <p className="text-xs text-muted-foreground">Rémunérations HT/TTC + bordereau PDF</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </motion.div>

            {/* KPI Tiles Dark Luxury avec interprétation humaine */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 14,
                    marginBottom: 24,
                }}
                data-testid="agent-kpi-tiles"
            >
                <KPIWithExplanation
                    label="CA · ce mois"
                    value={kpis.ca_month != null ? fmtEUR(kpis.ca_month).replace('€', '') : '—'}
                    unit="€"
                    explanation={caExplanation}
                    icon={Euro}
                    accent={kpis.ca_month > 0 ? 'green' : 'gray'}
                    dataTestid="kpi-ca-month"
                />
                <KPIWithExplanation
                    label="Mandats actifs"
                    value={kpis.active_mandates}
                    explanation={mandatesExplanation}
                    icon={Home}
                    accent="purple"
                    dataTestid="kpi-active-mandates"
                />
                <KPIWithExplanation
                    label="Atteinte objectif"
                    value={target.achievement_pct !== null ? target.achievement_pct : '—'}
                    unit={target.achievement_pct !== null ? '%' : ''}
                    explanation={targetExplanation}
                    icon={Target}
                    accent={target.on_track === true ? 'green' : target.on_track === false ? 'orange' : 'gray'}
                    dataTestid="kpi-target"
                />
                <KPIWithExplanation
                    label="À risque"
                    value={at_risk_count}
                    explanation={atRiskExplanation}
                    icon={AlertTriangle}
                    accent={at_risk_count >= 5 ? 'red' : at_risk_count > 0 ? 'orange' : 'green'}
                    dataTestid="kpi-at-risk"
                />
            </div>

            {/* Target progress bar (compact, conditional) */}
            {target.ca_target > 0 && target.achievement_pct !== null && (
                <div
                    data-testid="target-progress"
                    style={{
                        background: 'var(--homly-bg-elevated)',
                        border: '1px solid var(--homly-border)',
                        borderRadius: 'var(--homly-radius-md)',
                        padding: '14px 16px',
                        marginBottom: 24,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Target size={14} style={{ color: 'var(--homly-purple)' }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--homly-text-primary)', fontFamily: 'var(--homly-font-display)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                Objectif du mois
                            </span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--homly-text-primary)', fontFamily: 'var(--homly-font-mono)' }}>
                            {fmtEUR(target.ca_current)} / {fmtEUR(target.ca_target)}
                        </span>
                    </div>
                    <div style={{ position: 'relative', height: 8, background: 'var(--homly-bg-card-2)', borderRadius: 'var(--homly-radius-pill)', overflow: 'hidden' }}>
                        <div
                            style={{
                                position: 'absolute',
                                top: 0, bottom: 0, left: 0,
                                background: target.on_track
                                    ? 'linear-gradient(90deg, #22D3A2 0%, #34D399 100%)'
                                    : 'linear-gradient(90deg, #FF8A3D 0%, #FF5F57 100%)',
                                width: `${Math.min(100, target.achievement_pct)}%`,
                                transition: 'width 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                            }}
                        />
                        <div
                            title="Position attendue à ce jour"
                            style={{
                                position: 'absolute',
                                top: 0, bottom: 0,
                                width: 1,
                                background: 'rgba(255,255,255,0.5)',
                                left: `${Math.min(100, target.expected_pct_now)}%`,
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Tabs Dark Luxury */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList
                    data-testid="cockpit-tabs"
                    className="w-full flex md:grid md:grid-cols-5 h-auto p-1 overflow-x-auto gap-1 md:gap-0 [&>button]:shrink-0 [&>button]:whitespace-nowrap"
                    style={{
                        background: 'var(--homly-bg-elevated)',
                        border: '1px solid var(--homly-border)',
                        borderRadius: 'var(--homly-radius-md)',
                    }}
                >
                    <TabsTrigger
                        value="priorities"
                        data-testid="tab-priorities"
                        className="flex items-center gap-1.5 py-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#A855F7] data-[state=active]:text-white rounded-lg text-xs md:text-sm"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Priorités</span>
                        {(priorities.length + (at_risk_mandates?.length || 0)) > 0 && (
                            <span
                                className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                                style={{ background: 'var(--homly-orange-bg)', color: 'var(--homly-orange)' }}
                            >
                                {priorities.length + (at_risk_mandates?.length || 0)}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger
                        value="my-coaching"
                        data-testid="tab-my-coaching"
                        className="flex items-center gap-1.5 py-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF8A3D] data-[state=active]:text-white rounded-lg text-xs md:text-sm"
                    >
                        <Trophy className="w-3.5 h-3.5" />
                        <span>Coaching</span>
                    </TabsTrigger>
                    <TabsTrigger
                        value="activity"
                        data-testid="tab-activity"
                        className="flex items-center gap-1.5 py-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#A855F7] data-[state=active]:text-white rounded-lg text-xs md:text-sm"
                    >
                        <Activity className="w-3.5 h-3.5" />
                        <span>Activité</span>
                    </TabsTrigger>
                    <TabsTrigger
                        value="estimations"
                        data-testid="tab-estimations"
                        className="flex items-center gap-1.5 py-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF8A3D] data-[state=active]:to-[#E66A1A] data-[state=active]:text-white rounded-lg text-xs md:text-sm"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Découv.</span>
                    </TabsTrigger>
                </TabsList>

                {/* ── Priorités du jour + mandats à risque ─────────────── */}
                <TabsContent value="priorities" className="mt-5 space-y-5">
                    {/* Data Quality — pousse les agents à consigner visites/échanges pour alimenter leurs KPIs */}
                    <DataQualityBanner basePath={basePath} />

                    {/* Lot 5 — actions de réunion assignées à l'agent */}
                    <AgentActionsWidget />

                    {/* M8 — Bilan hebdomadaire de réussite (comparaison à soi) */}
                    <WeeklySuccessCard />

                    {/* HomlyScore widget */}
                    <HomlyScoreWidget basePath={basePath} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <SectionBlock
                            kicker="À faire aujourd'hui"
                            title="Priorités du jour"
                            helpText="Triées par impact business par l'IA. Cliquez pour préparer l'action."
                            rightSlot={<PriorityBadge priority={priorities.length > 0 ? 'high' : 'ok'} label={`${priorities.length}`} />}
                            dataTestid="priorities-panel"
                        >
                            {priorities.length === 0 ? (
                                <div
                                    style={{
                                        background: 'var(--homly-bg-elevated)',
                                        border: '1px solid var(--homly-border)',
                                        borderRadius: 'var(--homly-radius-lg)',
                                    }}
                                >
                                    <EmptyState
                                        icon={CheckCircle2}
                                        title="Tout est sous contrôle"
                                        description="Aucune action urgente détectée. Profitez-en pour prospecter ou préparer vos prochains RDV."
                                        accent="purple"
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {priorities.map((p, i) => {
                                        const Icon = ICON_MAP[p.icon] || Target;
                                        const priorityLevel = p.urgency >= 3 ? 'critical' : p.urgency === 2 ? 'high' : 'medium';
                                        return (
                                            <ActionCard
                                                key={i}
                                                priority={priorityLevel}
                                                icon={Icon}
                                                title={p.title}
                                                context={p.detail}
                                                ctaLabel="Voir"
                                                onCtaClick={() => p.link && navigate(p.link)}
                                                dataTestid={`priority-${i}`}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </SectionBlock>

                        <SectionBlock
                            kicker="À surveiller"
                            title="Mandats à risque"
                            helpText="Vendeurs silencieux ou mandats anciens. Un point rapide évite la dénonciation."
                            rightSlot={at_risk_count > 5 ? <span style={{ fontSize: 11, color: 'var(--homly-text-tertiary)', fontFamily: 'var(--homly-font-mono)' }}>+{at_risk_count - 5} autres</span> : null}
                            dataTestid="at-risk-panel"
                        >
                            {at_risk_mandates.length === 0 ? (
                                <div
                                    style={{
                                        background: 'var(--homly-bg-elevated)',
                                        border: '1px solid var(--homly-border)',
                                        borderRadius: 'var(--homly-radius-lg)',
                                    }}
                                >
                                    <EmptyState
                                        icon={CheckCircle2}
                                        title="Aucun mandat à risque"
                                        description="Tous vos vendeurs sont actifs et engagés. Continuez sur cette dynamique."
                                        accent="purple"
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        background: 'var(--homly-bg-elevated)',
                                        border: '1px solid var(--homly-border)',
                                        borderRadius: 'var(--homly-radius-lg)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {at_risk_mandates.map((m) => <RiskRow key={m.mandate_id} m={m} basePath={basePath} />)}
                                </div>
                            )}
                        </SectionBlock>
                    </div>
                </TabsContent>

                {/* ── Coaching IA ────────────────────────────────────── */}
                <TabsContent value="my-coaching" className="mt-5">
                    <MyCoachingTab user={agent} />
                </TabsContent>

                {/* ── Demander à l'IA : onglet supprimé → bouton header → AIChatModal ── */}

                {/* ── Activité 7 jours + visites ─────────────────────── */}
                <TabsContent value="activity" className="mt-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <div
                            data-testid="week-activity"
                            style={{
                                background: 'var(--homly-bg-elevated)',
                                border: '1px solid var(--homly-border)',
                                borderRadius: 'var(--homly-radius-lg)',
                                padding: 20,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                <div
                                    style={{
                                        width: 28, height: 28,
                                        borderRadius: 'var(--homly-radius-sm)',
                                        background: 'var(--homly-purple-bg)',
                                        color: 'var(--homly-purple)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Activity size={14} strokeWidth={2.2} />
                                </div>
                                <h2 style={{ margin: 0, fontFamily: 'var(--homly-font-display)', fontWeight: 700, fontSize: 14, color: 'var(--homly-text-primary)' }}>
                                    Mon activité — 7 jours
                                </h2>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
                                <span style={{ fontFamily: 'var(--homly-font-mono)', fontSize: 32, fontWeight: 700, color: 'var(--homly-text-primary)', letterSpacing: '-0.02em' }}>
                                    {week_activity.total}
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--homly-text-tertiary)' }}>actions cette semaine</span>
                            </div>
                            <div style={{ height: 1, background: 'var(--homly-border-subtle)', marginBottom: 14 }} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16, rowGap: 10, fontSize: 13 }}>
                                <ActivityLine label="Messages" value={week_activity.messages} />
                                <ActivityLine label="Relances" value={week_activity.follow_ups} />
                                <ActivityLine label="RDV / visites" value={week_activity.meetings} />
                            </div>
                        </div>

                        <div
                            data-testid="upcoming-visits"
                            style={{
                                background: 'var(--homly-bg-elevated)',
                                border: '1px solid var(--homly-border)',
                                borderRadius: 'var(--homly-radius-lg)',
                                padding: 20,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                <div
                                    style={{
                                        width: 28, height: 28,
                                        borderRadius: 'var(--homly-radius-sm)',
                                        background: 'var(--homly-orange-bg)',
                                        color: 'var(--homly-orange)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Calendar size={14} strokeWidth={2.2} />
                                </div>
                                <h2 style={{ margin: 0, fontFamily: 'var(--homly-font-display)', fontWeight: 700, fontSize: 14, color: 'var(--homly-text-primary)' }}>
                                    Prochaines visites
                                </h2>
                            </div>
                            {upcoming_visits.length === 0 ? (
                                <div style={{ fontSize: 13, color: 'var(--homly-text-tertiary)', textAlign: 'center', padding: '20px 0' }}>
                                    Aucune visite planifiée
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {upcoming_visits.map((v) => (
                                        <Link
                                            key={v.id}
                                            to={`${basePath}/mandates/v2/${v.mandate_id}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                padding: 10,
                                                borderRadius: 'var(--homly-radius-sm)',
                                                textDecoration: 'none',
                                                transition: 'background var(--homly-transition-fast)',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <div
                                                style={{
                                                    width: 40, height: 40,
                                                    borderRadius: 'var(--homly-radius-sm)',
                                                    background: 'var(--homly-purple-bg)',
                                                    color: 'var(--homly-purple)',
                                                    display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                    border: '1px solid var(--homly-purple-border)',
                                                }}
                                            >
                                                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    {v.scheduled_date ? new Date(v.scheduled_date).toLocaleDateString('fr-FR', { month: 'short' }) : ''}
                                                </span>
                                                <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1, fontFamily: 'var(--homly-font-mono)' }}>
                                                    {v.scheduled_date ? new Date(v.scheduled_date).getDate() : ''}
                                                </span>
                                            </div>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--homly-text-primary)', fontFamily: 'var(--homly-font-display)' }}>{v.visitor_name}</div>
                                                <div style={{ fontSize: 11.5, color: 'var(--homly-text-tertiary)' }}>{fmtDate(v.scheduled_date)}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* ── Estimations ────────────────────────────────────── */}
                <TabsContent value="estimations" className="mt-5">
                    <div
                        style={{
                            maxWidth: 560,
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-border)',
                            borderRadius: 'var(--homly-radius-lg)',
                            padding: 20,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <div
                                style={{
                                    width: 28, height: 28,
                                    borderRadius: 'var(--homly-radius-sm)',
                                    background: 'var(--homly-orange-bg)',
                                    color: 'var(--homly-orange)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <FileText size={14} strokeWidth={2.2} />
                            </div>
                            <h2 style={{ margin: 0, fontFamily: 'var(--homly-font-display)', fontWeight: 700, fontSize: 14, color: 'var(--homly-text-primary)' }}>
                                Mes découvertes de propriété
                            </h2>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <ActivityLine label="En attente de réponse" value={kpis.estimations_pending} />
                            <ActivityLine label="Brouillons" value={kpis.estimations_drafts} />
                        </div>
                        <HomlyButton
                            variant="outline"
                            size="sm"
                            fullWidth
                            onClick={() => navigate(`${basePath}/estimations`)}
                            dataTestid="see-estimations-btn"
                        >
                            Voir mes découvertes
                        </HomlyButton>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Chat IA centralisé — accessible depuis le header de tous les onglets */}
            <AIChatModal open={aiChatOpen} onClose={() => setAiChatOpen(false)} role="agent" />

            <GlossaryHint />

            {/* TermTooltip preserved imports — placed as hidden util for tour rendering parity */}
            <span style={{ display: 'none' }}>
                <TermTooltip term="pipeline">_</TermTooltip>
                <TermTooltip term="silence">_</TermTooltip>
            </span>
        </div>
    );
}

function ActivityLine({ label, value }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--homly-border-subtle)' }}>
            <span style={{ fontSize: 13, color: 'var(--homly-text-secondary)' }}>{label}</span>
            <span style={{ fontFamily: 'var(--homly-font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--homly-text-primary)' }}>{value}</span>
        </div>
    );
}
